'use client';

import { useCallback, useMemo, useRef, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { idx } from '../../../_lib/marketing-utils';

export interface DocsSearchLabels {
  label: string;
  placeholder: string;
  kbd: string;
  noteInitial: string;
  noteSubmitted: string;
  noResults: string;
  loading: string;
  contactCta: string;
}

/** Minimal-Form des Such-Index (strukturell kompatibel zu help-data SearchEntry). */
export interface DocsSearchEntry {
  title: string;
  description: string;
  categoryLabel: string;
  href: string;
  body?: string;
}

const MAX_RESULTS = 8;

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

/**
 * Docs-Suche. Mit `indexUrl` (DE-Landing): funktionaler Volltext-Client-Filter
 * über Titel/Beschreibung/Kategorie/Body mit Ergebnis-Dropdown – der Index wird
 * beim ersten Fokus lazy nachgeladen (hält die Landing leicht). Ohne `indexUrl`
 * (EN/FR, noch kein gerenderter Artikel-Index): nicht-funktionaler Platzhalter.
 */
export function DocsSearch({
  labels,
  indexUrl,
  contactHref,
}: {
  labels: DocsSearchLabels;
  indexUrl?: string;
  contactHref: string;
}) {
  if (!indexUrl) {
    return <DocsSearchPlaceholder labels={labels} />;
  }

  return (
    <DocsSearchLive
      labels={labels}
      indexUrl={indexUrl}
      contactHref={contactHref}
    />
  );
}

function DocsSearchLive({
  labels,
  indexUrl,
  contactHref,
}: {
  labels: DocsSearchLabels;
  indexUrl: string;
  contactHref: string;
}) {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [entries, setEntries] = useState<DocsSearchEntry[]>([]);
  const loadStarted = useRef(false);

  // Index erst beim ersten Fokus holen – Nutzer, die nie suchen, laden nichts.
  const loadIndex = useCallback(() => {
    if (loadStarted.current) {
      return;
    }
    loadStarted.current = true;

    void fetch(indexUrl)
      .then((res) => (res.ok ? res.json() : []))
      .then((data: DocsSearchEntry[]) => setEntries(data))
      .catch(() => {
        loadStarted.current = false; // Retry beim nächsten Fokus erlauben.
      });
  }, [indexUrl]);

  const trimmed = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (!trimmed) {
      return [];
    }

    const terms = trimmed.split(/\s+/);

    return entries
      .map((entry) => {
        const title = entry.title.toLowerCase();
        const meta =
          `${entry.description} ${entry.categoryLabel}`.toLowerCase();
        const body = (entry.body ?? '').toLowerCase();
        const haystack = `${title} ${meta} ${body}`;

        if (!terms.every((term) => haystack.includes(term))) {
          return null;
        }

        // Ranking: Titel-Treffer > Beschreibung/Kategorie > nur Body.
        const score = terms.reduce((sum, term) => {
          if (title.includes(term)) return sum + 3;
          if (meta.includes(term)) return sum + 2;
          return sum + 1;
        }, 0);

        return { entry, score };
      })
      .filter(
        (hit): hit is { entry: DocsSearchEntry; score: number } => hit !== null,
      )
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_RESULTS)
      .map((hit) => hit.entry);
  }, [trimmed, entries]);

  return (
    <div className="docs-search reveal" style={idx(3)}>
      <form
        role="search"
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          if (results[0]) {
            router.push(results[0].href);
          }
        }}
      >
        <label className="sr-only" htmlFor="docsSearchInput">
          {labels.label}
        </label>
        <div className="docs-search-field">
          <SearchIcon />
          <input
            id="docsSearchInput"
            type="search"
            placeholder={labels.placeholder}
            value={query}
            onFocus={loadIndex}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
          />
        </div>
      </form>

      {trimmed ? (
        results.length > 0 ? (
          <ul className="docs-search-results">
            {results.map((result) => (
              <li key={result.href}>
                <Link className="docs-search-result" href={result.href}>
                  <span className="docs-search-result-title">
                    {result.title}
                  </span>
                  <span className="docs-search-result-cat">
                    {result.categoryLabel}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : entries.length === 0 ? (
          <p className="docs-search-note">{labels.loading}</p>
        ) : (
          <p className="docs-search-note">
            {labels.noResults}{' '}
            <Link href={contactHref} className="docs-search-contact">
              {labels.contactCta}
            </Link>
          </p>
        )
      ) : null}
    </div>
  );
}

/** EN/FR-Fallback: nicht-funktionaler Platzhalter (Volltext-Suche folgt). */
function DocsSearchPlaceholder({ labels }: { labels: DocsSearchLabels }) {
  const [note, setNote] = useState(labels.noteInitial);

  return (
    <form
      className="docs-search reveal"
      style={idx(3)}
      role="search"
      autoComplete="off"
      onSubmit={(event) => {
        event.preventDefault();
        setNote(labels.noteSubmitted);
      }}
    >
      <label className="sr-only" htmlFor="docsSearchInput">
        {labels.label}
      </label>
      <div className="docs-search-field">
        <SearchIcon />
        <input
          id="docsSearchInput"
          type="search"
          placeholder={labels.placeholder}
          aria-describedby="docsSearchNote"
        />
        <span className="docs-search-kbd" aria-hidden="true">
          {labels.kbd}
        </span>
      </div>
      <p className="docs-search-note" id="docsSearchNote">
        {note}
      </p>
    </form>
  );
}
